import { useEffect, useState } from 'react'
import { HomeDashboardService } from './services/home-dashboard'

// New home dashboard (GET Dashboard/Home).
//
// Who never gets it - their menus, landing pages and screens stay exactly as they are:
//  - Cintas (CompanyId 34930): hard-coded legacy sidebar + "Audit Report" task screen.
//  - Franchise users (DashboardStyle.DashboardId 2): land on /app/dashboard/groups.
// Everyone else gets it only when their company switched it on: the backend default is
// "classic" (= today's task dashboard) until a company admin saves a dashboard type in Settings.

export const HOME_DASHBOARD_PATH = '/app/home'
export const CLASSIC_DASHBOARD_PATH = '/app/task'
export const HOME_DASHBOARD_SETTINGS_TAB = 'home-dashboard'

const LEGACY_COMPANY_IDS = [34930]
const FRANCHISE_DASHBOARD_ID = 2
const STATUS_TTL_MS = 5 * 60 * 1000
const LANDING_TIMEOUT_MS = 3000

export const isHomeDashboardEligible = user => {
    if (!user) return false
    if (LEGACY_COMPANY_IDS.includes(Number(user.CompanyId))) return false
    if (Number(user?.DashboardStyle?.DashboardId) === FRANCHISE_DASHBOARD_ID) return false
    return true
}

export const isHomeDashboardAdmin = user =>
    Boolean(user?.IsSuperAdmin || user?.IsAdmin === true || Number(user?.RoleId) === 3)

const UNAVAILABLE = Object.freeze({ available: false, layout: 'classic', canEdit: false })

// One status per (company, user) for a few minutes: the sidebar, the task screen banner and the
// Settings screen all ask, and GET user/Dashboard/Setting is cheap but not free.
let cache = { key: '', at: 0, promise: null, value: null }
const listeners = new Set()

const statusKey = user => `${user?.CompanyId || 0}:${user?.unique_id || user?.UserId || 0}`

const toStatus = res => {
    if (!res || res.NewHomeAvailable === false) return UNAVAILABLE
    const type = String(res?.Profile?.Type || '').toLowerCase()
    return {
        available: true,
        layout: type && type !== 'classic' ? 'home' : 'classic',
        canEdit: res.CanEdit === true,
        defaultLayout: res.DefaultLayout || 'classic',
        layoutSource: res?.Profile?.LayoutSource || 'default',
    }
}

export const getHomeDashboardStatus = (user, { force = false } = {}) => {
    if (!isHomeDashboardEligible(user)) return Promise.resolve(UNAVAILABLE)
    const key = statusKey(user)
    const fresh = cache.key === key && Date.now() - cache.at < STATUS_TTL_MS
    if (!force && fresh && cache.promise) return cache.promise
    const promise = HomeDashboardService.getSetting()
        .then(toStatus)
        // 403 = company not allowed by the backend gate; anything else = keep the classic page.
        .catch(() => UNAVAILABLE)
        .then(value => {
            if (cache.promise === promise) {
                cache.value = value
                listeners.forEach(fn => fn(value))
            }
            return value
        })
    cache = { key, at: Date.now(), promise, value: cache.key === key ? cache.value : null }
    return promise
}

// Called after the Settings tab saves, so the sidebar's Home item follows at once.
export const refreshHomeDashboardStatus = user => getHomeDashboardStatus(user, { force: true })

export const useHomeDashboardStatus = user => {
    const eligible = isHomeDashboardEligible(user)
    const key = statusKey(user)
    const [status, setStatus] = useState(() =>
        eligible && cache.key === key && cache.value ? cache.value : null,
    )
    useEffect(() => {
        if (!eligible) {
            setStatus(UNAVAILABLE)
            return undefined
        }
        let alive = true
        const onChange = value => {
            if (alive) setStatus(value)
        }
        listeners.add(onChange)
        getHomeDashboardStatus(user).then(onChange)
        return () => {
            alive = false
            listeners.delete(onChange)
        }
    }, [eligible, key])
    return status
}

// After login: the classic target stays as it is unless the company switched the new home on.
// Never throws - any failure keeps the classic landing page.
export const resolveHomeLanding = async (user, target) => {
    if (target !== CLASSIC_DASHBOARD_PATH || !isHomeDashboardEligible(user)) return target
    try {
        // Sign-in never waits long for this: after 3 s the classic page opens (the status can be
        // slow on a cold server cache). The call keeps running and fills the cache for the sidebar.
        const timeout = new Promise(resolve => {
            setTimeout(() => resolve(UNAVAILABLE), LANDING_TIMEOUT_MS)
        })
        const status = await Promise.race([getHomeDashboardStatus(user, { force: true }), timeout])
        return status.available && status.layout === 'home' ? HOME_DASHBOARD_PATH : target
    } catch {
        return target
    }
}

// "Try the new home dashboard" banner on the task list (admins, company still on classic).
const bannerKey = user => `eauditor.homeDashboardBanner.dismissed.${user?.CompanyId || 0}`

export const isHomeBannerDismissed = user => {
    try {
        return localStorage.getItem(bannerKey(user)) === '1'
    } catch {
        return false
    }
}

export const dismissHomeBanner = user => {
    try {
        localStorage.setItem(bannerKey(user), '1')
    } catch {
        // storage blocked: the banner just shows again next time
    }
}
